/**
 * Common brightness management capabilities
 * Used by dimmers and RGBW lights
 */

/**
 * Set brightness action
 */
export function createSetBrightnessAction() {
	return {
		name: 'Set Brightness',
		description: 'Set the brightness level',
		parameters: {
			brightness: {
				type: 'number',
				required: true,
				min: 0,
				max: 100,
				unit: '%',
				description: 'Brightness level (0-100%)',
			},
			transition: {
				type: 'number',
				required: false,
				min: 0,
				max: 10000,
				unit: 'ms',
				default: 0,
				description: 'Transition duration in milliseconds',
			},
		},
		executeAction: async (device, channel, params) => {
			const { brightness, transition = 0 } = params;
			const channelId = parseInt(channel.split(':')[1]) || 0;

			return {
				method: 'Light.Set',
				params: {
					id: channelId,
					brightness,
					transition: transition / 1000, // Convert ms to seconds
				},
			};
		},
	};
}

/**
 * Brightness level condition
 */
export function createBrightnessLevelCondition() {
	return {
		name: 'Brightness Level',
		description: 'Check current brightness level',
		parameters: {
			operator: {
				type: 'enum',
				required: true,
				values: ['eq', 'gt', 'lt', 'gte', 'lte', 'between'],
				description: 'Comparison operator',
			},
			brightness: {
				type: 'number',
				required: true,
				min: 0,
				max: 100,
				unit: '%',
				description: 'Brightness threshold',
			},
			brightnessMax: {
				type: 'number',
				required: false,
				min: 0,
				max: 100,
				unit: '%',
				description: 'Maximum brightness (for between operator)',
			},
			tolerance: {
				type: 'number',
				required: false,
				default: 2,
				unit: '%',
				description: 'Tolerance for comparison',
			},
		},
		evaluateCondition: (deviceState, channel, params) => {
			const lightId = parseInt(channel.split(':')[1]);
			const currentBrightness = deviceState.status?.['light:' + lightId]?.brightness || 0;
			const { operator, brightness, brightnessMax, tolerance = 2 } = params;

			switch (operator) {
				case 'eq':
					return Math.abs(currentBrightness - brightness) <= tolerance;
				case 'gt':
					return currentBrightness > brightness + tolerance;
				case 'lt':
					return currentBrightness < brightness - tolerance;
				case 'gte':
					return currentBrightness >= brightness;
				case 'lte':
					return currentBrightness <= brightness;
				case 'between':
					return (
						currentBrightness >= brightness && currentBrightness <= (brightnessMax || 100)
					);
				default:
					return false;
			}
		},
	};
}

/**
 * Brightness changed trigger
 */
export function createBrightnessChangedTrigger() {
	return {
		name: 'Brightness Changed',
		description: 'Fires when brightness changes by more than threshold',
		parameters: {
			threshold: {
				type: 'number',
				required: false,
				default: 5,
				min: 1,
				max: 100,
				unit: '%',
				description: 'Minimum brightness change to trigger',
			},
		},
		detectTrigger: (previousState, currentState, channel, params) => {
			const lightId = parseInt(channel.split(':')[1]);
			const prevBrightness = previousState?.status?.['light:' + lightId]?.brightness || 0;
			const currBrightness = currentState?.status?.['light:' + lightId]?.brightness || 0;
			const { threshold = 5 } = params;

			return Math.abs(currBrightness - prevBrightness) >= threshold;
		},
	};
}
